import { Grid, TextField, Button } from "@mui/material";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from "react";
import { RequestService } from "services/RequestService";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '& label': {
    color: theme.palette.text.secondary,
  },
  '& label.Mui-focused': {
    color: theme.palette.info.main,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[600],
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.info.main,
    },
  },
}));

function CompanyForm({ formData, handleChange, showNotification }) {
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    validateForm(formData);
  }, [formData]);

  const validateForm = (data) => {
    const newErrors = {};
    
    // Required fields validation
    if (!data.name?.trim()) {
      newErrors.name = 'اسم الشركة مطلوب';
    }

    if (!data.email?.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!data.website?.trim()) {
      newErrors.website = 'الموقع الإلكتروني مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchUsers = async () => {
    try {
      // get just company admins
      const response = await RequestService.get("Auth/get-all-users?userType=3");
      if (response.data && !response.data.error) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const renderFileUpload = (fieldName, label) => {
    // Determine accept type based on field name
    const acceptType = fieldName === 'logoUrl' ? 'image/*' : 
                      (fieldName === 'enProfileUrl' || fieldName === 'arProfileUrl') ? 'application/pdf' : '*/*';
    
    // Custom validation message based on field type
    const fileTypeMessage = fieldName === 'logoUrl' ? 'يجب أن يكون الملف صورة' : 
                          (fieldName === 'enProfileUrl' || fieldName === 'arProfileUrl') ? 'يجب أن يكون الملف PDF' : '';

    const handleFileValidation = (event) => {
      const file = event.target.files[0];
      if (file) {
        if (fieldName === 'logoUrl') {
          if (!file.type.startsWith('image/')) {
            showNotification(fileTypeMessage, "error");
            event.target.value = '';
            return;
          }
          if (file.size > 2 * 1024 * 1024) { // 2MB limit for logo
            showNotification('الشعار كبير جدًا، يجب أن يكون حجمه أقل من 2MB', "error");
            event.target.value = '';
            return;
          }
        }
        if (fieldName === 'enProfileUrl' || fieldName === 'arProfileUrl') {
          if (file.type !== 'application/pdf') {
            showNotification(fileTypeMessage, "error");
            event.target.value = '';
            return;
          }
          if (file.size > 25 * 1024 * 1024) { // 25MB limit for PDFs
            showNotification('الملف كبير جدًا، يجب أن يكون حجمه أقل من 25MB', "error");
            event.target.value = '';
            return;
            }
        }
        handleChange({ ...formData, [fieldName]: file });
      }
    };

    return (
      <MDBox>
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          sx={{ 
            width: '100%', 
            height: '56px',
            color: formData[fieldName] ? 'text.primary' : 'text.secondary',
            borderColor: 'grey.400',
            '&:hover': {
              borderColor: 'grey.600',
              backgroundColor: 'grey.100',
            },
            display: 'flex',
            justifyContent: 'flex-start',
            textAlign: 'left',
            paddingLeft: '14px'
          }}
        >
          {formData[fieldName] ? (
            typeof formData[fieldName] === 'string' ? 
            formData[fieldName].split('/').pop() : 
            formData[fieldName].name
          ) : `اختر ${label}`}
          <VisuallyHiddenInput 
            type="file" 
            onChange={handleFileValidation} 
            accept={acceptType}
          />
        </Button>
        {formData[fieldName] && (
          <MDTypography variant="caption" color="info" sx={{ mt: 0.5, display: 'block' }}>
            {typeof formData[fieldName] === 'string' ? 
             formData[fieldName].split('/').pop() : 
             formData[fieldName].name}
          </MDTypography>
        )}
      </MDBox>
    );
  };

  
  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <StyledTextField
          fullWidth
          label="اسم الشركة"
          name="name"
          value={formData.name}
          onChange={(e) => handleChange({ ...formData, name: e.target.value })}
          required
          placeholder="أدخل اسم الشركة"
          error={!!errors.name}
          helperText={errors.name}
        />
      </Grid>

      <Grid item xs={12}>
        <StyledTextField
          fullWidth
          label="البريد الإلكتروني"
          name="email"
          value={formData.email}
          onChange={(e) => handleChange({ ...formData, email: e.target.value })}
          required
          placeholder="example@company.com"
          error={!!errors.email}
          helperText={errors.email}
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          fullWidth
          label="الموقع الإلكتروني"
          name="website"
          value={formData.website}
          onChange={(e) => handleChange({ ...formData, website: e.target.value })}
          required
          placeholder="www.example.com"
          error={!!errors.website}
          helperText={errors.website}
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          fullWidth
          label="الوصف"
          name="description"
          multiline
          rows={4}
          value={formData.description}
          onChange={(e) => handleChange({ ...formData, description: e.target.value })}
          placeholder="أدخل وصف الشركة"
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="رقم الهاتف"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={(e) => handleChange({ ...formData, phoneNumber: e.target.value })}
          placeholder="0xxxxxxxxx"
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="الرقم المختصر"
          name="shortNumber"
          value={formData.shortNumber}
          onChange={(e) => handleChange({ ...formData, shortNumber: e.target.value })}
          placeholder="xxxx"
        />
      </Grid>
      <Grid item xs={12}>
        {renderFileUpload("logoUrl", "شعار الشركة")}
      </Grid>
      <Grid item xs={6}>
        {renderFileUpload("enProfileUrl", "الملف التعريفي باللغة الإنجليزية")}
      </Grid>
      <Grid item xs={6}>
        {renderFileUpload("arProfileUrl", "الملف التعريفي باللغة العربية")}
      </Grid>
    </Grid>
  );
}

CompanyForm.propTypes = {
  formData: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    website: PropTypes.string,
    description: PropTypes.string,
    phoneNumber: PropTypes.string,
    shortNumber: PropTypes.string,
    logoUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    enProfileUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    arProfileUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

export default CompanyForm;